/**
 * UnsubscribeDialog — modern, focused dialog used from the Inbox conversation
 * bar, Patient profile header, and Patient Lookup drawer. Tags the contact to
 * trigger GHL unsubscribe workflows.
 *
 * Clinic campaigns tag the contact in the ORIGIN location. HQ campaigns look
 * up the contact in the configured HQ GHL sub-account (email → phone → name)
 * and tag the matched record there.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Loader2, Lock, Eye, EyeOff, UserMinus, AlertCircle, CheckCircle2,
  Building2, Globe2, XCircle, Ban, CalendarX, UserX, BookOpen, Compass,
  History, Shield, Info, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { unsubscribeApi } from '../../lib/unsubscribeApi';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const CAMPAIGN_ICON = {
  cancellation: CalendarX,
  no_show: UserX,
  pt_journey: Compass,
  pt_status: Ban,
  ebook: BookOpen,
};

// Short, staff-facing explanation of what each campaign actually covers.
// Lives in the UI only — the backend tag mapping is the source of truth.
const CAMPAIGN_INFO = {
  cancellation: 'Cancellation flow — reminders and follow-ups sent after a patient cancels an appointment.',
  no_show: 'No-show flow — re-engagement sequence sent after a patient misses an appointment.',
  pt_journey: 'Patient Journey — includes the current journey with clinic, straight-to-assessment, assessment-without-treatment, and other journey-stage nudges.',
  pt_status: 'Patient Status — covers Active, New Lead, Drop-off, and Discharge campaigns.',
  ebook: 'E-Books — download sequences for shoulder pain, back pain, and neck pain guides.',
};

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
}

// ─── Campaign row ─────────────────────────────────────────────────────────
function CampaignRow({ campaign, selected, onToggle, result, disabled, previouslyApplied }) {
  const Icon = CAMPAIGN_ICON[campaign.key] || XCircle;
  const resOk = result && result.ok;
  const resFail = result && !result.ok;
  return (
    <button
      type="button"
      onClick={() => !disabled && onToggle(campaign.key)}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'w-full group flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/40',
        selected
          ? 'bg-rose-50/70 ring-1 ring-rose-200 shadow-[0_1px_2px_rgba(244,63,94,0.05)]'
          : previouslyApplied
            ? 'bg-emerald-50/60 ring-1 ring-emerald-200 hover:ring-emerald-300'
            : 'bg-white ring-1 ring-slate-200/70 hover:ring-slate-300 hover:bg-slate-50/60',
        disabled && 'opacity-60 cursor-not-allowed',
      )}
      data-testid={`unsubscribe-campaign-${campaign.key}`}
    >
      <div
        className={cn(
          'h-8 w-8 rounded-lg flex items-center justify-center shrink-0 transition-colors',
          selected
            ? 'bg-rose-500 text-white'
            : previouslyApplied
              ? 'bg-emerald-500 text-white'
              : 'bg-slate-100 text-slate-500',
        )}
      >
        <Icon size={14} strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight leading-tight truncate">{campaign.label}</div>
          {CAMPAIGN_INFO[campaign.key] && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
                    className="shrink-0 inline-flex items-center justify-center h-4 w-4 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200 cursor-help"
                    aria-label={`About ${campaign.label}`}
                    data-testid={`unsubscribe-info-${campaign.key}`}
                  >
                    <Info size={11} strokeWidth={2.2} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" className="max-w-[260px] text-[11.5px] leading-snug">
                  {CAMPAIGN_INFO[campaign.key]}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {previouslyApplied && !resOk && !resFail && (
          <div className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 mt-1" data-testid={`unsubscribe-already-${campaign.key}`}>
            <CheckCircle2 size={11} />
            Already unsubscribed
            {previouslyApplied.by ? <> by <span className="font-semibold">{previouslyApplied.by}</span></> : null}
            {previouslyApplied.when ? <span className="text-emerald-600/70"> · {previouslyApplied.when}</span> : null}
          </div>
        )}
        {resOk && (
          <div className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 mt-1" data-testid={`unsubscribe-res-ok-${campaign.key}`}>
            <CheckCircle2 size={11} /> Applied
          </div>
        )}
        {resFail && (
          <div className="flex items-center gap-1 text-[11px] font-medium text-rose-600 mt-1" title={result.error} data-testid={`unsubscribe-res-fail-${campaign.key}`}>
            <AlertCircle size={11} /> {result.error}
          </div>
        )}
      </div>
      <div
        className={cn(
          'h-5 w-5 rounded-md flex items-center justify-center shrink-0 transition-all',
          selected
            ? 'bg-rose-500 text-white'
            : previouslyApplied
              ? 'bg-emerald-500 text-white'
              : 'bg-white ring-1 ring-slate-300 group-hover:ring-slate-400',
        )}
        data-testid={`unsubscribe-cb-${campaign.key}`}
      >
        {(selected || (previouslyApplied && !selected)) && <CheckCircle2 size={13} strokeWidth={3} />}
      </div>
    </button>
  );
}

// ─── Section header ───────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, title, tone }) {
  const dot = tone === 'hq' ? 'bg-indigo-500' : 'bg-emerald-500';
  const text = tone === 'hq' ? 'text-indigo-600' : 'text-emerald-600';
  return (
    <div className="flex items-center gap-2 px-1">
      <span className={cn('h-1.5 w-1.5 rounded-full', dot)} aria-hidden />
      <Icon size={11} className={text} strokeWidth={2.3} />
      <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-700">{title}</span>
    </div>
  );
}

// ─── History drawer (right-side slide-out panel) ──────────────────────────
function HistoryDrawer({ history, loading, open }) {
  return (
    <div
      className={cn(
        'border-l border-slate-200/70 bg-slate-50/60 overflow-hidden transition-[width,opacity] duration-300 ease-out flex flex-col',
        open ? 'w-[300px] opacity-100' : 'w-0 opacity-0',
      )}
      aria-hidden={!open}
      data-testid="unsubscribe-history-drawer"
    >
      {/* Header — matches the main dialog header's vertical rhythm. The
          close affordance lives in the shared top-right (clock toggles). */}
      <div className="shrink-0 px-5 pt-6 pb-4 border-b border-slate-200/60">
        <div className="flex items-center gap-2 text-slate-500 mb-2">
          <History size={13} strokeWidth={2.3} />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.16em]">Activity</span>
        </div>
        <div className="text-[13px] font-semibold text-slate-900 tracking-tight leading-tight">
          {loading
            ? 'Loading…'
            : `${history.length} event${history.length === 1 ? '' : 's'}`}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5">
        {loading && (
          <div className="flex items-center gap-2 text-[11.5px] text-slate-400 py-2">
            <Loader2 size={12} className="animate-spin" /> Loading recent activity…
          </div>
        )}
        {!loading && history.length === 0 && (
          <div className="text-[11.5px] italic text-slate-400 py-2">
            No prior unsubscribes for this contact.
          </div>
        )}
        {history.map((row, i) => (
          <div
            key={i}
            className="rounded-lg bg-white ring-1 ring-slate-200/60 px-2.5 py-2 shadow-[0_1px_1px_rgba(15,23,42,0.02)]"
            data-testid={`unsubscribe-history-row-${i}`}
          >
            <div className="flex items-start gap-2">
              <div
                className={cn(
                  'h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5',
                  row.success ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600',
                )}
              >
                {row.success ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-800 leading-tight">
                  <span className="truncate">{row.campaign_label || row.campaign || '—'}</span>
                  {row.scope && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-1 py-0.5 rounded">
                      {row.scope}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  by <strong className="text-slate-700 font-medium">{row.user_name || 'Unknown'}</strong>
                  <span className="text-slate-400"> · {fmtWhen(row.created_at)}</span>
                </div>
                {!row.success && row.error && (
                  <div className="text-[10.5px] text-rose-500 mt-0.5 truncate" title={row.error}>{row.error}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────
export function UnsubscribeDialog({ open, onOpenChange, contactId, contactName, contactEmail, contactPhone, locationId }) {
  const [campaigns, setCampaigns] = useState({ clinic: [], hq: [] });
  const [selected, setSelected] = useState({});
  const [accessCode, setAccessCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadHistory = () => {
    if (!contactId) return;
    setHistoryLoading(true);
    unsubscribeApi
      .listContactAudits(contactId, { limit: 10 })
      .then((r) => setHistory(r.data?.audits || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setResults(null);
    setAccessCode('');
    setShowCode(false);
    setSelected({});
    setHistoryOpen(false);
    setLoading(true);
    unsubscribeApi
      .listCampaigns()
      .then((r) => setCampaigns(r.data || { clinic: [], hq: [] }))
      .catch((e) => {
        toast.error('Failed to load campaigns');
        if (e?.response?.status === 403) onOpenChange(false);
      })
      .finally(() => setLoading(false));
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId, onOpenChange]);

  const toggle = (key) => setSelected((p) => ({ ...p, [key]: !p[key] }));
  const selectedKeys = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected],
  );

  const submit = async () => {
    if (!selectedKeys.length) { toast.error('Pick at least one campaign'); return; }
    if (!accessCode.trim()) { toast.error('Enter the access code'); return; }
    setSubmitting(true);
    setResults(null);
    try {
      const r = await unsubscribeApi.apply({
        contact_id: contactId,
        origin_location_id: locationId,
        campaigns: selectedKeys,
        access_code: accessCode.trim(),
        contact_name: contactName || '',
        contact_email: contactEmail || '',
        contact_phone: contactPhone || '',
      });
      setResults(r.data?.results || []);
      if (r.data?.success) toast.success('Unsubscribed from selected campaigns');
      else toast.warning('Some campaigns could not be applied — see details');
      loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (e?.response?.status === 403) toast.error(detail || 'Invalid access code');
      else toast.error('Submit failed: ' + detail);
    } finally {
      setSubmitting(false);
    }
  };

  const resultFor = (key) => (results || []).find((r) => r.campaign === key);
  const allApplied = results && results.length > 0 && results.every((r) => r.ok);

  // Map of campaign_key → latest successful audit for this contact. Powers
  // the green "Already unsubscribed" state on each campaign row.
  const previouslyApplied = useMemo(() => {
    const m = {};
    for (const row of history || []) {
      if (!row.success) continue;
      const k = row.campaign;
      if (!k || m[k]) continue; // history already sorted desc; keep first (latest)
      m[k] = { when: fmtWhen(row.created_at), by: row.user_name || '' };
    }
    return m;
  }, [history]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden rounded-2xl border-slate-200/60 shadow-xl transition-[max-width] duration-300 ease-out',
          // Hide shadcn's built-in close so we can render clock + X as
          // perfectly-aligned sibling buttons inside our own header.
          '[&>button.absolute]:hidden',
          historyOpen ? 'max-w-[760px]' : 'max-w-[460px]',
        )}
        data-testid="unsubscribe-dialog"
      >
        <DialogTitle className="sr-only">Unsubscribe {contactName || 'contact'} from campaigns</DialogTitle>

        {/* Shared header controls — sit above the two-column body so both
            columns share the exact same button rhythm. */}
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  aria-pressed={historyOpen}
                  aria-label="View unsubscribe history"
                  className={cn(
                    'h-7 w-7 rounded-md flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300',
                    historyOpen
                      ? 'bg-slate-100 text-slate-900 ring-1 ring-slate-200'
                      : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100',
                  )}
                  data-testid="unsubscribe-history-toggle"
                >
                  <History size={14} strokeWidth={2.2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="text-[11.5px]">
                {historyOpen ? 'Hide history' : 'View history'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            data-testid="unsubscribe-close"
          >
            <X size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="flex items-stretch">
          {/* Left: main form */}
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4">
              <div className="flex items-center gap-2 text-rose-600 mb-2">
                <UserMinus size={14} strokeWidth={2.4} />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.16em]">Unsubscribe</span>
              </div>
              <h2 className="text-[19px] font-bold tracking-tight text-slate-900 leading-tight">
                Remove from campaigns
              </h2>
              {contactName && (
                <p className="text-[12.5px] text-slate-500 mt-1 leading-snug">
                  Updating <span className="font-semibold text-slate-700">{contactName}</span>
                </p>
              )}
            </div>

            {/* Body */}
            <div className="px-6 pb-4 max-h-[64vh] overflow-y-auto space-y-4 flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-slate-400">
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  <span className="text-[12.5px]">Loading campaigns…</span>
                </div>
              ) : (
                <>
                  {/* Clinic section */}
                  {(campaigns.clinic || []).length > 0 && (
                    <div className="space-y-2" data-testid="unsubscribe-group-clinic">
                      <SectionHeader icon={Building2} title="Clinic Location" tone="clinic" />
                      <div className="space-y-1.5">
                        {campaigns.clinic.map((c) => (
                          <CampaignRow
                            key={c.key}
                            campaign={c}
                            selected={!!selected[c.key]}
                            onToggle={toggle}
                            result={resultFor(c.key)}
                            disabled={submitting}
                            previouslyApplied={previouslyApplied[c.key]}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* HQ section */}
                  {(campaigns.hq || []).length > 0 && (
                    <div className="space-y-2" data-testid="unsubscribe-group-hq">
                      <SectionHeader icon={Globe2} title="HQ Location" tone="hq" />
                      <div className="space-y-1.5">
                        {campaigns.hq.map((c) => (
                          <CampaignRow
                            key={c.key}
                            campaign={c}
                            selected={!!selected[c.key]}
                            onToggle={toggle}
                            result={resultFor(c.key)}
                            disabled={submitting}
                            previouslyApplied={previouslyApplied[c.key]}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Access code */}
                  <div className="pt-1">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Shield size={11} className="text-slate-400" />
                      <label htmlFor="unsub-access-code" className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-700">
                        Access Code
                      </label>
                      <span className="text-[10.5px] text-slate-400 ml-auto">Required</span>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        id="unsub-access-code"
                        type={showCode ? 'text' : 'password'}
                        value={accessCode}
                        onChange={(e) => setAccessCode(e.target.value)}
                        placeholder="Enter the shared code"
                        className="h-10 pl-9 pr-10 bg-slate-50/80 border-slate-200/70 text-[13px] placeholder:text-slate-400 focus:bg-white focus:ring-rose-200"
                        disabled={submitting}
                        autoComplete="off"
                        data-testid="unsubscribe-access-code"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCode((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        tabIndex={-1}
                        aria-label={showCode ? 'Hide code' : 'Show code'}
                      >
                        {showCode ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 border-t border-slate-100 flex items-center justify-between gap-3">
              <div className="text-[11.5px] text-slate-500" aria-live="polite">
                {selectedKeys.length > 0 && !allApplied && (
                  <>{selectedKeys.length} campaign{selectedKeys.length === 1 ? '' : 's'} selected</>
                )}
                {allApplied && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircle2 size={12} /> All applied
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={submitting}
                  className="text-[12.5px] font-medium text-slate-500 hover:text-slate-800 px-2 py-1.5 rounded"
                  data-testid="unsubscribe-cancel"
                >
                  Cancel
                </button>
                <Button
                  onClick={submit}
                  disabled={submitting || loading || !selectedKeys.length || !accessCode.trim()}
                  className="h-9 px-4 gap-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-[12.5px] font-semibold shadow-sm"
                  data-testid="unsubscribe-submit"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus size={13} />}
                  Unsubscribe{selectedKeys.length ? ` · ${selectedKeys.length}` : ''}
                </Button>
              </div>
            </div>
          </div>

          {/* Right: history drawer */}
          <HistoryDrawer
            history={history}
            loading={historyLoading}
            open={historyOpen}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default UnsubscribeDialog;
